import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import { MediaGroup } from './helpers';
import MediaCard from './MediaCard';

interface GroupedBrowseTabProps {
  groups: MediaGroup[];
  activeItem: MediaLibraryItem | null;
  togglingFavouriteId: string | null;
  mode: 'manage' | 'select';
  onFileClick: (mediaId: string) => void;
  onToggleFavourite: (mediaId: string, current: boolean) => void;
  /** Explicit checkbox selection (falls back to onFileClick when absent). */
  onToggleSelect?: (mediaId: string) => void;
  selectedIds?: string[];
}

/**
 * Grouped-by-run view — collapsible date/run sections, each rendering the same
 * unified MediaCard the flat grid uses. This replaces the old clickable
 * `<div onClick>` tiles + off-system `#ffd700` star, so the grouped view shares
 * the grid's accessibility (role=button cells, keyboard, focus ring) and visual
 * language (indigo selection / amber favourite).
 */
const GroupedBrowseTab: React.FC<GroupedBrowseTabProps> = ({
  groups,
  activeItem,
  togglingFavouriteId,
  mode,
  onFileClick,
  onToggleFavourite,
  onToggleSelect,
  selectedIds = [],
}) => {
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const activeMediaId = activeItem ? activeItem.media_id || activeItem.id : '';
  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  if (groups.length === 0) {
    return (
      <div className="media-state-card">
        <p className="media-state-title">No media yet</p>
        <p>No media items found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => {
        const isCollapsed = collapsed[group.key] ?? false;
        const dateStr = group.date
          ? new Date(group.date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : null;
        const headerLabel = dateStr ? `${group.label} · ${dateStr}` : group.label;

        return (
          <div key={group.key}>
            <button
              type="button"
              onClick={() => toggleCollapse(group.key)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left bg-[var(--surface-2)] border border-[var(--border-subtle)]"
              aria-expanded={!isCollapsed}
            >
              <span className="text-[10px] text-[var(--app-text-muted)]" aria-hidden="true">
                {isCollapsed ? '▶' : '▼'}
              </span>
              <span className="flex-1 text-xs font-semibold uppercase tracking-widest">
                {headerLabel}
              </span>
              <span className="text-[10px] text-[var(--app-text-muted)]">
                {group.items.length} item{group.items.length !== 1 ? 's' : ''}
              </span>
            </button>

            {!isCollapsed && (
              <div
                className="media-grid mt-2"
                style={{ ['--media-cell' as string]: '110px' } as React.CSSProperties}
                role="grid"
                aria-label={headerLabel}
              >
                {group.items.map((item) => {
                  const mediaId = item.media_id || item.id;
                  return (
                    <div role="gridcell" key={mediaId}>
                      <MediaCard
                        item={item}
                        selected={mode === 'select' ? selectedSet.has(mediaId) : mediaId === activeMediaId}
                        selectMode={mode === 'select'}
                        togglingFavourite={togglingFavouriteId === mediaId}
                        onActivate={onFileClick}
                        onToggleSelect={onToggleSelect || onFileClick}
                        onToggleFavourite={onToggleFavourite}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GroupedBrowseTab;
